import { create } from "zustand";
import { Milestone } from "@/backend/models/milestone";
import { MilestoneFrontendController } from "../controllers/milestoneController";
import { CreateMilestoneRequest, UpdateMilestoneRequest } from "@/types/milestoneTypes";

const initialState: { milestones: Milestone[] } = {
  milestones: [],
};

export interface MilestoneStore {
  milestones: Milestone[];
  setMilestones: (milestones: Milestone[]) => void;
  fetchMilestonesByProjectId: (projectId: string) => Promise<void>;
  createMilestone: (data: CreateMilestoneRequest) => Promise<Milestone | null>;
  updateMilestone: (id: string, data: UpdateMilestoneRequest) => Promise<Milestone | null>;
  deleteMilestone: (id: string) => Promise<boolean>;
  reset: () => void;
}

export const useMilestoneStore = create<MilestoneStore>((set) => ({
  ...initialState,

  setMilestones: (milestones: Milestone[]) => set({ milestones }),

  fetchMilestonesByProjectId: async (projectId: string) => {
    const controller = new MilestoneFrontendController();
    const response = await controller.readAllByProjectId(projectId);
    if (response.status === 200) {
      useMilestoneStore.getState().setMilestones(response.milestones.map((m) => new Milestone(m)));
    }
  },

  createMilestone: async (data: CreateMilestoneRequest) => {
    const controller = new MilestoneFrontendController();
    const response = await controller.create(data);

    if (response.status === 201 && response.milestone) {
      const milestone = new Milestone(response.milestone);
      const current = useMilestoneStore.getState().milestones;
      useMilestoneStore.getState().setMilestones([...current, milestone]);
      return milestone;
    }
    return null;
  },

  updateMilestone: async (id: string, data: UpdateMilestoneRequest) => {
    const controller = new MilestoneFrontendController();
    const response = await controller.update(id, data);

    if (response.status === 200 && response.milestone) {
      const milestone = new Milestone(response.milestone);
      const current = useMilestoneStore.getState().milestones;
      useMilestoneStore.getState().setMilestones(current.map((m) => m.id === id ? milestone : m));
      return milestone;
    }
    return null;
  },

  deleteMilestone: async (id: string) => {
    const controller = new MilestoneFrontendController();
    const response = await controller.delete(id);

    if (response.status === 200) {
      const current = useMilestoneStore.getState().milestones;
      useMilestoneStore.getState().setMilestones(current.filter((m) => m.id !== id));
      return true;
    }
    return false;
  },

  reset: () => set({ ...initialState }),
}));
